import { useAuth } from '@agentnet/auth';
import {
  ChatInput,
  ConversationItem,
  DrawerComponent,
  DrawerContent,
  DrawerExit,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@fluentsms/agentnet-web-components';
import { NotesType } from '@fluentsms/agentnet-web-components/dist/types/chat';
import { Button, createStyles, makeStyles, Tab, Tabs, Theme } from '@material-ui/core';
import {
  getDocumentContentApi,
  getRemittanceReceivableNotes,
  uploadRemittanceReceivableNote,
} from 'api/orderManagement/order-management-api';
import { ApiStatus } from 'features/orderManagement/enum';
import { convertFileToBase64, downloadFile } from 'features/orderManagement/fileUtils';
import { NotesDocumentDataType, UploadRemittanceReceivableNotesRequestModel } from 'features/orderManagement/types';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import useAsync from 'hooks/useAsync';
import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { NoMessages } from 'ui-kit/components/NoMessages';
import { getFullName } from 'utilities/utilities';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tabContent: {
      height: 'calc(100% - 64px)',
      overflow: 'hidden',
      overflowY: 'auto',
      paddingLeft: theme.spacing(2),
      paddingTop: theme.spacing(2),
    },
    chatInput: {
      display: 'flex',
      width: '97%',
      paddingLeft: theme.spacing(3),
    },
    conversationItem: {
      // Fix text wrapping in conversation bubbles
      // Override the width: max-content that causes overflow
      '& div': {
        maxWidth: '100%',
        boxSizing: 'border-box',
      },
      // Target the conversation bubble specifically (div with padding that contains text)
      '& > div > div > div:last-of-type': {
        width: 'auto !important',
        maxWidth: '100% !important',
      },
      // Ensure text wraps properly
      '& p, & span': {
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
      },
    },
  }),
);

interface RemittanceReceivableNotesType extends NotesType {
  uploadedFileNameText?: string;
}

interface RemittanceReceivableOrderDrawerProps {
  remitReceivableId: number;
  onClose: () => void;
}

export function RemittanceReceivableOrderDrawer({ remitReceivableId, onClose }: RemittanceReceivableOrderDrawerProps) {
  const classes = useStyles();
  const { getAccessToken } = useAuth();
  const [tabValue, setTabValue] = useState<number>(0);
  const [notes, setNotes] = useState<RemittanceReceivableNotesType[]>([]);
  const [currentDownloadFile, setCurrentDownloadFile] = useState<{ uploadFileName: string; fileName: string } | null>(
    null,
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getRemittanceReceivableNotesApi = async (remittanceReceivableId: number): Promise<NotesType[]> => {
    const token = await getAccessToken();
    const notes = await getRemittanceReceivableNotes(token, remittanceReceivableId);

    const noteTypes: NotesType[] = notes.map((note) => ({
      createdByUserId: note.createdByUserId,
      userName: getFullName({ firstName: note.createdByFirstName, lastName: note.createdByLastName }),
      attachments: note.fileNameText
        ? [
            {
              fileName: note.fileNameText,
              uploadedFileName: note.fileNameText,
              file: new File(['content'], note.fileNameText || ''),
            },
          ]
        : undefined,
      createdDate: note.createdDate || '',
      uploadedFileNameText: note.uploadedFileNameText,
      notes: note.notes,
    }));
    return noteTypes;
  };

  const {
    execute: executeGetRemittanceReceivableNotes,
    value: getRemittanceReceivableNotesValue,
    status: getRemittanceReceivableNotesStatus,
  } = useAsync<RemittanceReceivableNotesType[]>(getRemittanceReceivableNotesApi, false);

  const uploadRemittanceReceivableNoteApi = async (
    request: UploadRemittanceReceivableNotesRequestModel,
  ): Promise<void> => {
    const token = await getAccessToken();
    await uploadRemittanceReceivableNote(token, request);
  };

  const { execute: executeUploadRemittanceReceivableNote } = useAsync<void>(uploadRemittanceReceivableNoteApi, false);

  const getDocumentData = async (documentName: string): Promise<NotesDocumentDataType> => {
    const token = await getAccessToken();
    const response = await getDocumentContentApi(documentName, token);
    return response;
  };

  const {
    execute: executeGetDocumentData,
    status: getDocumentDataStatus,
    value: getDocumentDataResult,
  } = useAsync<NotesDocumentDataType>(getDocumentData, false);

  const isFetchingFile = getDocumentDataStatus === 'pending';

  const onTabChange = (e: ChangeEvent<unknown>, newValue: number): void => {
    setTabValue(newValue);
  };

  const onSubmit = async (message: string, attachments: File[]) => {
    const attachment = attachments?.length ? attachments[0] : null;

    let uploadFileString = '';
    if (attachment) {
      uploadFileString = await convertFileToBase64(attachment);
    }

    const request: UploadRemittanceReceivableNotesRequestModel = {
      remitReceivableId: remitReceivableId,
      notes: message,
      fileName: attachment?.name || '',
      uploadFileString: uploadFileString,
    };

    await executeUploadRemittanceReceivableNote(request);

    if (attachment) {
      await executeGetRemittanceReceivableNotes(remitReceivableId);
    } else {
      const userName = profileCtx.getFullNameFromProfile?.() || '';

      const newNote: NotesType = {
        createdByUserId: profileCtx.profile ? profileCtx.profile.userId : 0,
        createdByUserName: userName,
        userName: userName,
        attachments: undefined,
        createdDate: new Date().toISOString(),
        notes: message,
      };
      setNotes([...notes, newNote]);
    }

    scrollToBottom();
  };

  const onFileClick = (fileName: string) => {
    if (!fileName || isFetchingFile) return;

    const found = notes.find((n) => n.attachments && n.attachments[0]?.fileName === fileName);
    if (found && found.attachments?.length) {
      setCurrentDownloadFile({ uploadFileName: found.uploadedFileNameText || '', fileName });
      executeGetDocumentData(found.uploadedFileNameText || '');
    }
  };

  useEffect(() => {
    if (remitReceivableId) {
      executeGetRemittanceReceivableNotes(remitReceivableId);
    }
  }, [remitReceivableId]);

  useEffect(() => {
    if (getRemittanceReceivableNotesStatus === ApiStatus.Success && getRemittanceReceivableNotesValue) {
      setNotes(getRemittanceReceivableNotesValue);
    }
  }, [getRemittanceReceivableNotesStatus, getRemittanceReceivableNotesValue]);

  useEffect(() => {
    scrollToBottom();
  }, [notes, remitReceivableId]);

  useEffect(() => {
    if (getDocumentDataStatus === ApiStatus.Success && getDocumentDataResult && currentDownloadFile) {
      downloadFile(getDocumentDataResult.documentData, currentDownloadFile.fileName);
      setCurrentDownloadFile(null);
    }
  }, [getDocumentDataStatus, getDocumentDataResult]);

  return (
    <>
      <DrawerComponent isDrawerOpen={remitReceivableId ? true : false}>
        <DrawerHeader
          content={<DrawerTitle title={`Remittance Receivable ID : ${remitReceivableId}`} />}
          contentRight={
            <DrawerExit
              onClick={() => {
                onClose();
              }}
            />
          }
          divider
          subHeader={
            <Tabs aria-label="conversation logs" indicatorColor="primary" value={tabValue} onChange={onTabChange}>
              <Tab aria-controls="conversation-panel" id="conversation-tab" label="Conversation" />
            </Tabs>
          }
        />
        <DrawerContent>
          <>
            <div className={classes.tabContent}>
              {notes.length ? (
                notes.map((note, index: number) => {
                  return (
                    <div key={index} className={classes.conversationItem}>
                      <ConversationItem
                        conversation={note}
                        profileUserId={profileCtx.profile?.userId}
                        onFileClick={onFileClick}
                      />
                    </div>
                  );
                })
              ) : (
                <NoMessages />
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className={classes.chatInput}>
              <ChatInput acceptTypes="image/*, .pdf, .doc, .docx, .xls, .xlsx, .txt" onSubmit={onSubmit} />
            </div>
          </>
        </DrawerContent>
        <DrawerFooter>
          <Button color="primary" variant="contained" onClick={onClose}>
            Done
          </Button>
        </DrawerFooter>
      </DrawerComponent>
    </>
  );
}
